import type { GetChildListParams } from '@repo/api/parent/parentService';
import type { ParentGroupFilter } from '@repo/types/parent';

import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export type ParentSortOrder = 'ASC' | 'DESC';

export interface ParentListParams {
  search: string;
  sortOrder: ParentSortOrder;
  groupId: ParentGroupFilter | undefined;
  pageFor: (childId: number) => number;
  setSearch: (value: string) => void;
  setSortOrder: (value: ParentSortOrder) => void;
  setGroupId: (value: ParentGroupFilter | undefined) => void;
  setPage: (childId: number, page: number) => void;
  buildParamsFor: (child: { id: number }) => GetChildListParams;
}

const PAGE_PREFIX = 'page_';

// Page is per child: each list page fans out one query per child.
export function pageParamKey(childId: number): string {
  return `${PAGE_PREFIX}${childId}`;
}

function parseGroupId(raw: string | null): ParentGroupFilter | undefined {
  if (raw === null) return undefined;
  if (raw === 'none') return 'none';
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function parsePage(raw: string | null): number {
  if (raw === null) return 0;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
}

function deletePageParams(params: URLSearchParams): void {
  for (const key of [...params.keys()]) {
    if (key.startsWith(PAGE_PREFIX)) {
      params.delete(key);
    }
  }
}

export function useParentListParams(): ParentListParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('q') ?? '';
  const sortOrder: ParentSortOrder = searchParams.get('sort') === 'asc' ? 'ASC' : 'DESC';
  const groupId = parseGroupId(searchParams.get('groupId'));

  const pageFor = useCallback((childId: number) => parsePage(searchParams.get(pageParamKey(childId))), [searchParams]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const trimmed = value.trim();
          if (trimmed) {
            prev.set('q', trimmed);
          } else {
            prev.delete('q');
          }
          deletePageParams(prev);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSortOrder = useCallback(
    (value: ParentSortOrder) => {
      setSearchParams(
        (prev) => {
          if (value === 'ASC') {
            prev.set('sort', 'asc');
          } else {
            prev.delete('sort');
          }
          deletePageParams(prev);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setGroupId = useCallback(
    (value: ParentGroupFilter | undefined) => {
      setSearchParams(
        (prev) => {
          if (value === 'none' || (typeof value === 'number' && Number.isInteger(value) && value > 0)) {
            prev.set('groupId', String(value));
          } else {
            prev.delete('groupId');
          }
          deletePageParams(prev);
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPage = useCallback(
    (childId: number, page: number) => {
      setSearchParams(
        (prev) => {
          if (Number.isInteger(page) && page > 0) {
            prev.set(pageParamKey(childId), String(page));
          } else {
            prev.delete(pageParamKey(childId));
          }
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const buildParamsFor = useCallback(
    (child: { id: number }): GetChildListParams => {
      const params: GetChildListParams = {
        page: pageFor(child.id),
        sortOrder,
      };
      const name = search.trim();
      if (name) {
        params.name = name;
      }
      if (groupId !== undefined) {
        params.groupId = groupId;
      }
      return params;
    },
    [pageFor, sortOrder, search, groupId]
  );

  return {
    search,
    sortOrder,
    groupId,
    pageFor,
    setSearch,
    setSortOrder,
    setGroupId,
    setPage,
    buildParamsFor,
  };
}
